import React from 'react';
import styled from 'styled-components';
import { Copy, Link2, Pencil, Tag, Trash2 } from 'lucide-react';
import colors from 'theme/colors';
import SalesBar from '../_sales/SalesBar';
import { panelAccent } from 'theme/panelAccent';
import {
  STATUS,
  statusChip,
  tone,
  toneForPercent,
  isSellingOut,
  percentOf,
  displayPercent,
  formatInt,
} from '../_sales/salesTokens';

const applicationLabel = {
  ticket: 'Ingressos',
  product: 'Produtos',
  both: 'Ingressos e produtos',
};

/* "Ativo" em vez do "Disponível" dos ingressos: aqui o produtor liga e desliga o
   cupom por um switch com esse mesmo nome no formulário. */
export const couponStatusLabel = {
  [STATUS.AVAILABLE]: 'Ativo',
  [STATUS.SOLD_OUT]: 'Esgotado',
  [STATUS.DISABLED]: 'Desativado',
};

/**
 * Status do cupom pela mesma escala de status usada no painel de vendas.
 *
 * "Esgotado" deixou de ser vermelho: um cupom todo resgatado é campanha que deu
 * certo, não erro. Vermelho fica reservado para a ação destrutiva do rodapé.
 */
export function statusOf(data) {
  const maxUses = Number(data?.max_uses) || 0;
  const uses = Number(data?.uses) || 0;

  if (maxUses > 0 && maxUses - uses < 1) return STATUS.SOLD_OUT;
  if (!data?.is_active) return STATUS.DISABLED;

  return STATUS.AVAILABLE;
}

export default function DiscountCodeCard({ data, onDelete, onEdit, onCopy, onCopyLink }) {
  const code = data?.code || '';
  const maxUses = Number(data?.max_uses) || 0;
  const uses = Number(data?.uses) || 0;
  const remaining = Math.max(maxUses - uses, 0);
  const percent = percentOf(uses, maxUses);

  const status = statusOf(data);
  const chip = statusChip[status];

  /* Cupom desativado não tem desempenho a comunicar — o tom neutro evita que ele
     dispute atenção com os que estão de fato circulando. */
  const cardTone = status === STATUS.DISABLED ? tone.none : tone[toneForPercent(percent)];

  const application = applicationLabel[data?.discount_type];

  return (
    <Card $tone={cardTone}>
      <Accent $tone={cardTone} aria-hidden='true' />

      <Body>
        <Head>
          {/* O código é o que o produtor precisa levar para fora da tela, então
              ele é o próprio botão de copiar — sem caça ao ícone de 14px. */}
          <CodeChip
            type='button'
            onClick={onCopy}
            title={`Copiar o código ${code}`}
            aria-label={`Copiar o código ${code}`}
          >
            <CodeText>{code}</CodeText>
            <Copy size={14} strokeWidth={2.5} aria-hidden='true' />
          </CodeChip>

          <StatusChip $chip={chip}>{couponStatusLabel[status]}</StatusChip>
        </Head>

        <DiscountRow>
          <DiscountValue>{data?.total_discount}</DiscountValue>
          <DiscountCaption>de desconto</DiscountCaption>
        </DiscountRow>

        {application && (
          <TypeChip>
            <Tag size={12} strokeWidth={2.5} aria-hidden='true' />
            {application}
          </TypeChip>
        )}

        <Usage>
          <SalesBar
            percent={percent}
            tone={cardTone}
            height={8}
            label={`${formatInt(uses)} de ${formatInt(maxUses)} usos`}
          />

          {/* Três linhas de "Qnt." viraram uma frase: usado, total e o que sobra,
              que é o número que decide se dá para seguir divulgando o cupom. */}
          <UsageCaption>
            <span>
              <strong>{formatInt(uses)}</strong> de {formatInt(maxUses)} usados
              <Dot aria-hidden='true'>·</Dot>
              {remaining > 0 ? (
                /* Quase no fim é hora de aumentar o limite, não de descobrir pelo
                   comprador que o cupom parou de funcionar. */
                <Remaining $low={isSellingOut(percent)}>
                  {formatInt(remaining)} {isSellingOut(percent) ? 'restantes — acabando' : 'restantes'}
                </Remaining>
              ) : 'nenhum restante'}
            </span>

            {maxUses > 0 && <Percent>{displayPercent(percent)}%</Percent>}
          </UsageCaption>
        </Usage>

        <Actions>
          <ActionButton type='button' onClick={onCopyLink} title='Copiar o link do evento com o cupom já aplicado'>
            <Link2 size={14} strokeWidth={2.5} aria-hidden='true' />
            Copiar link
          </ActionButton>

          {onEdit && (
            <ActionButton type='button' onClick={onEdit}>
              <Pencil size={14} strokeWidth={2.5} aria-hidden='true' />
              Editar
            </ActionButton>
          )}

          {onDelete && (
            <DangerButton
              type='button'
              onClick={onDelete}
              title={`Excluir o código ${code}`}
              aria-label={`Excluir o código ${code}`}
            >
              <Trash2 size={15} strokeWidth={2.5} aria-hidden='true' />
            </DangerButton>
          )}
        </Actions>
      </Body>
    </Card>
  );
}

/* ─────────────────────────────  Shell  ───────────────────────────── */

const Card = styled.article`
  display: flex;
  align-items: stretch;
  width: 100%;
  border-radius: 14px;
  border: 1px solid ${({ $tone }) => $tone.border};
  background: ${colors.neutralLight};
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 18px ${({ $tone }) => $tone.accent}22;
    border-color: ${({ $tone }) => $tone.accent};
  }
`;

/* Mesma faixa lateral dos cards de desempenho: dá para varrer a lista e ver
   quais cupons estão circulando sem ler número nenhum. */
const Accent = styled.div`
  width: 4px;
  flex-shrink: 0;
  background: ${({ $tone }) => $tone.accent};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  padding: 14px 16px 12px;
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const CodeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px dashed #d8c3f5;
  background: #f7efff;
  color: #6d14cc;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  svg {
    flex-shrink: 0;
    opacity: 0.75;
  }

  &:hover {
    background: #f0e2ff;
    border-color: ${panelAccent(colors.secondaryPurple)};

    svg {
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: 2px solid ${panelAccent(colors.secondaryPurple)};
    outline-offset: 2px;
  }
`;

const CodeText = styled.span`
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${({ $chip }) => $chip.bg};
  color: ${({ $chip }) => $chip.text};
  border: 1px solid ${({ $chip }) => $chip.border};
`;

/* ─────────────────────────────  Desconto  ───────────────────────────── */

const DiscountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const DiscountValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.5px;
  color: ${colors.nightDark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DiscountCaption = styled.span`
  font-size: 12px;
  color: ${colors.night};
  white-space: nowrap;
`;

const TypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  background: ${colors.neutralBackground};
  border: 1px solid #dfe7f5;
  color: ${colors.night};
  font-size: 11px;
  font-weight: 600;
  align-self: flex-start;
  width: fit-content;
`;

/* ─────────────────────────────  Uso  ───────────────────────────── */

const Usage = styled.div`
  margin-top: 14px;
`;

const UsageCaption = styled.p`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 0 0;
  font-size: 12px;
  color: ${colors.night};

  strong {
    color: ${colors.nightDark};
    font-weight: 600;
  }
`;

const Remaining = styled.span`
  color: ${({ $low }) => ($low ? tone.slow.text : colors.night)};
  font-weight: ${({ $low }) => ($low ? 700 : 400)};
`;

const Dot = styled.span`
  margin: 0 6px;
  color: ${colors.nightLight};
`;

const Percent = styled.span`
  flex-shrink: 0;
  font-weight: 600;
  color: ${colors.nightDark};
`;

/* ─────────────────────────────  Ações  ───────────────────────────── */

const Actions = styled.footer`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #eceef2;
`;

/* Rótulo visível em vez de quatro ícones de 14px no canto: o alvo passa a ter
   32px de altura (WCAG 2.5.5) e "editar" deixa de ser vizinho cego de "excluir". */
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: ${colors.night};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f7efff;
    border-color: #e5d5f8;
    color: #6d14cc;
  }

  &:focus-visible {
    outline: 2px solid ${panelAccent(colors.secondaryPurple)};
    outline-offset: 2px;
  }
`;

const DangerButton = styled(ActionButton)`
  margin-left: auto;
  width: 32px;
  padding: 0;
  color: ${colors.night};

  &:hover {
    background: #fdf0f1;
    border-color: #f4d3d6;
    color: #b3242e;
  }

  &:focus-visible {
    outline-color: ${colors.danger};
  }
`;
